// Formations (Ordre 11) — pure helpers of the catalogue of Jamora's features. Unit-tested in training.test.js.
// The server decides every right and rule; these only filter, group, label and prepare requests.

import { errorMessage } from "./api";

export const TRAINING_VIEW_ALL = "training:view_all";
export const TRAINING_MANAGE_ALL = "training:manage_all";
export const TRAINING_MANAGE_CATALOG = "training:manage_catalog";
export const TRAINING_KEYS = [TRAINING_VIEW_ALL, TRAINING_MANAGE_ALL, TRAINING_MANAGE_CATALOG];
// Reading the catalogue (decision 6 of 18/10/2026): any training key, or whoever works on missions.
export const CATALOG_READ_KEYS = [...TRAINING_KEYS, "missions:view", "missions:view_all", "missions:manage", "missions:manage_all",
  "missions:execute", "missions:manage_types"];

const fold = (text) => (text || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Every filter is optional; words of the search must all appear in the code, label or description (accents ignored).
export function filterFeatures(features, { search = "", status = "active", source = "", role = "", section = "" } = {}) {
  const words = fold(search).split(/\s+/).filter(Boolean);
  return features.filter((f) => {
    if (status && f.status !== status) return false;
    if (source && f.source !== source) return false;
    if (role && !(f.audience || []).includes(role)) return false;
    if (section && f.section_code !== section) return false;
    const hay = fold(`${f.code} ${f.label} ${f.description || ""}`);
    return words.every((w) => hay.includes(w));
  });
}

// Sections in their order, each with its (already filtered) features; a section left empty by the filters is dropped.
export function groupBySection(sections, features, { keepEmpty = false } = {}) {
  const bySection = new Map(sections.map((s) => [s.code, []]));
  for (const f of features) {
    if (!bySection.has(f.section_code)) bySection.set(f.section_code, []);
    bySection.get(f.section_code).push(f);
  }
  const known = new Map(sections.map((s) => [s.code, s]));
  return [...bySection.entries()]
    .map(([code, items]) => ({ section: known.get(code) || { code, label: code }, features: items }))
    .filter((group) => keepEmpty || group.features.length > 0);
}

export function roleLabels(roles, codes) {
  const labels = new Map((roles || []).map((r) => [r.code, r.label]));
  return (codes || []).map((c) => labels.get(c) || c);
}

// Only what changed is sent; the page of a Jamora feature is never sent (it comes from Jamora's code).
export function buildFeaturePatch(original, draft) {
  const patch = { expected_version: original.version };
  if (draft.label.trim() !== original.label) patch.label = draft.label.trim();
  if ((draft.description || "").trim() !== (original.description || "")) patch.description = (draft.description || "").trim() || null;
  if (draft.section_code !== original.section_code) patch.section_code = draft.section_code;
  const audience = [...draft.audience].sort();
  if (audience.join(",") !== [...(original.audience || [])].sort().join(",")) patch.audience = draft.audience;
  if (original.source === "crm" && (draft.route || "").trim() !== (original.route || "")) patch.route = (draft.route || "").trim() || null;
  return patch;
}

export function isPatchEmpty(patch) {
  return Object.keys(patch).every((k) => k === "expected_version");
}

// « Copie de Jamora du 18 oct. 2026 · f6e04a44 »: which version of Jamora's code the catalogue was seeded from.
export function copyLabel(copy) {
  if (!copy?.commit) return "";
  const date = copy.commit_date ? new Date(copy.commit_date).toLocaleDateString("fr-FR", { day: "numeric", month: "short", year: "numeric" }) : "";
  return `Copie de Jamora${date ? ` du ${date}` : ""} · ${copy.commit.slice(0, 8)}`;
}

// Features a skill aims at, with a readable label (a code the catalogue no longer knows stays visible as its code).
export function featureChoices(features, selected) {
  const chosen = new Set(selected || []);
  return (features || []).filter((f) => f.status === "active" || chosen.has(f.code));
}

// --- Modules and programmes (Ordre 11-2) --------------------------------------------------------------------------------------------
export const VERSION_STATUS_LABELS = { draft: "Brouillon", published: "Publiée", deprecated: "Remplacée" };
export const AUDIENCE_KINDS = { pharmacy: "Personnel des pharmacies", internal: "Équipe Jamora (formation interne)" };

// The server's message for a refused request ({code, msg}), else the generic one.
export function trainingError(error, fallback) {
  return error?.response?.data?.detail?.msg || errorMessage(error, fallback);
}

// « 45 min », « 1 h », « 1 h 30 »; nothing for an unknown duration.
export function formatMinutes(minutes) {
  if (!minutes) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (!h) return `${m} min`;
  return m ? `${h} h ${String(m).padStart(2, "0")}` : `${h} h`;
}

// « Vendeur, Préparateur » or « Équipe Jamora »: who a programme is for.
export function audienceLabel(program, roles) {
  if (program.audience_kind === "internal") return "Équipe Jamora";
  const names = roleLabels(roles, program.audience);
  return names.length ? names.join(", ") : "Toutes les pharmacies";
}

const newKey = () => `k-${Math.random().toString(16).slice(2)}${Date.now().toString(16)}`;

// The module form's local state; objectives keep their server id (results of the sessions point at it) plus a local key for React.
export function moduleForm(module) {
  const m = module || {};
  return {
    title: m.title || "",
    objectives: (m.objectives || []).map((o) => ({ ...o, key: o.id })),
    feature_codes: [...(m.feature_codes || [])],
    duration_minutes: m.duration_minutes ? String(m.duration_minutes) : "",
    prerequisites: m.prerequisites || "",
    materials: m.materials || "",
    support_url: m.support_url || "",
    exercises: m.exercises || "",
    pass_threshold: m.pass_threshold ? String(m.pass_threshold) : "",
  };
}

export function newObjective(text = "") {
  return { key: newKey(), text };
}

const toNumber = (value) => (String(value).trim() === "" ? null : Number(value));

// The first problem the server would also refuse, in the person's words; null when the form can be sent.
export function moduleFormProblem(form) {
  if (form.title.trim().length < 2) return "Donnez un titre au module (2 caractères au moins).";
  const texts = form.objectives.map((o) => o.text.trim().toLowerCase()).filter(Boolean);
  if (new Set(texts).size !== texts.length) return "Un objectif est écrit deux fois.";
  const duration = toNumber(form.duration_minutes);
  if (duration !== null && (!Number.isInteger(duration) || duration < 5 || duration > 2400)) return "La durée se compte en minutes, de 5 à 2 400.";
  const threshold = toNumber(form.pass_threshold);
  if (threshold !== null && (!Number.isInteger(threshold) || threshold < 1 || threshold > 100)) return "Le seuil de réussite va de 1 à 100 %.";
  const url = form.support_url.trim();
  if (url && !/^https?:\/\/[^\s/]+\S*$/i.test(url)) return "Le lien du support doit commencer par https://";
  return null;
}

function modulePayload(form) {
  return {
    title: form.title.trim(),
    objectives: form.objectives.filter((o) => o.text.trim()).map((o) => (o.id ? { id: o.id, text: o.text.trim() } : { text: o.text.trim() })),
    feature_codes: form.feature_codes,
    duration_minutes: toNumber(form.duration_minutes),
    prerequisites: form.prerequisites.trim() || null,
    materials: form.materials.trim() || null,
    support_url: form.support_url.trim() || null,
    exercises: form.exercises.trim() || null,
    pass_threshold: toNumber(form.pass_threshold),
  };
}

// Creation: the whole form. Edition: only what changed, with the version read (a colleague's change in between = 409).
export function buildModuleRequest(form, original) {
  const payload = modulePayload(form);
  if (!original) return payload;
  const before = modulePayload(moduleForm(original));
  const patch = { expected_version: original.version };
  for (const key of Object.keys(payload)) {
    if (JSON.stringify(payload[key]) !== JSON.stringify(before[key])) patch[key] = payload[key];
  }
  return patch;
}

export function moveEntry(list, index, delta) {
  const target = index + delta;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

// Mirrors training/rules.py: what stops a draft from being saved (or published) before the round-trip.
export function programDraftProblem(entries, modulesById, { publishing = false } = {}) {
  const ids = entries.map((e) => e.module_id);
  if (new Set(ids).size !== ids.length) return "Un module figure deux fois.";
  if (!publishing) return null;
  if (!entries.length) return "Ajoutez au moins un module avant de publier.";
  for (const entry of entries) {
    const module = modulesById[entry.module_id];
    if (!module) return "Un module de ce programme n'existe plus : retirez-le.";
    if (module.active === false) return `Le module « ${module.title} » est désactivé : retirez-le ou réactivez-le.`;
    if (!(module.objectives || []).length) return `Le module « ${module.title} » n'a aucun objectif.`;
  }
  return null;
}

// Totals shown in the editor's preview, from the items as displayed (same rule as the server's rules.totals).
export function programTotals(items) {
  const required = items.filter((i) => i.required);
  const sum = (list) => list.reduce((acc, i) => acc + (i.duration_minutes || 0), 0);
  return {
    module_count: items.length, required_count: required.length,
    objective_count: items.reduce((acc, i) => acc + (i.objectives || []).length, 0),
    required_minutes: sum(required), total_minutes: sum(items),
  };
}

// « 3 modules (2 obligatoires) · 7 objectifs · 2 h 15 obligatoires »
export function programSummary(totals) {
  if (!totals?.module_count) return "Aucun module";
  const parts = [`${totals.module_count} module${totals.module_count > 1 ? "s" : ""}`
    + (totals.required_count !== totals.module_count ? ` (${totals.required_count} obligatoire${totals.required_count > 1 ? "s" : ""})` : "")];
  if (totals.objective_count) parts.push(`${totals.objective_count} objectif${totals.objective_count > 1 ? "s" : ""}`);
  if (totals.required_minutes) parts.push(`${formatMinutes(totals.required_minutes)}${totals.total_minutes !== totals.required_minutes ? " obligatoires" : ""}`);
  return parts.join(" · ");
}

export function filterByWords(items, search, text) {
  const words = fold(search).split(/\s+/).filter(Boolean);
  return items.filter((item) => words.every((w) => fold(text(item)).includes(w)));
}

// --- Sessions (Ordre 11-3) ------------------------------------------------------------------------------------------------------------
export const SESSION_STATUS = {
  prepared: { label: "Préparée", className: "bg-slate-100 text-slate-700" },
  in_progress: { label: "En cours", className: "bg-amber-50 text-amber-800" },
  closed: { label: "Clôturée", className: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "Annulée", className: "bg-slate-100 text-slate-500 line-through" },
};
export const SESSION_MODES = { on_site: "Sur place", visio: "Visio", internal: "Interne" };
export const ATTENDANCE = [
  { value: "present", label: "Présent" },
  { value: "partial", label: "En partie" },
  { value: "absent", label: "Absent" },
];
export const ATTENDANCE_LABELS = { invited: "Présence à indiquer", present: "Présent", partial: "En partie", absent: "Absent" };
export const RESULTS = [
  { value: "acquired", label: "Acquis", short: "Acquis", className: "bg-emerald-600 text-white border-emerald-600" },
  { value: "partial", label: "Partiellement acquis", short: "Partiel", className: "bg-amber-500 text-white border-amber-500" },
  { value: "not_acquired", label: "Non acquis", short: "Non acquis", className: "bg-red-600 text-white border-red-600" },
  { value: "not_assessed", label: "Non évalué", short: "Non évalué", className: "bg-slate-500 text-white border-slate-500" },
];
export const ATTENDED = ["present", "partial"];
export const SESSION_OPEN = ["prepared", "in_progress"];
// Preparing a session: training:manage_all, or organising a mission (the server checks which mission).
export const SESSION_CREATE_KEYS = [TRAINING_MANAGE_ALL, "missions:manage", "missions:manage_all"];

// « jeu. 5 nov. 2026 · 9 h 30 »
export function sessionWhen(session) {
  if (!session?.date) return "";
  const [y, m, d] = session.date.split("-").map(Number);
  const day = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short", year: "numeric", timeZone: "UTC" });
  return session.start_time ? `${day} · ${session.start_time.replace(":", " h ")}` : day;
}

// Objectives to assess for a participant: those of the modules marked as covered (all of the programme while none is marked yet).
export function objectivesToAssess(content, covered) {
  const modules = (covered || []).length ? content.filter((item) => covered.includes(item.module_id)) : content;
  return modules.map((item) => ({ module: item, objectives: item.objectives || [] }));
}

// « 3 / 5 objectifs notés » for a participant, over the objectives to assess.
export function assessedCount(participant, content, covered) {
  const ids = objectivesToAssess(content, covered).flatMap((g) => g.objectives.map((o) => o.id));
  return { done: ids.filter((id) => participant.results?.[id]).length, total: ids.length };
}

// What closing will do: the server's blocking problems, and how many attended participants still have objectives without a result
// (closing records them as « non évalué »).
export function closingSummary(session) {
  const covered = session.covered_module_ids || [];
  const missing = (session.participants || []).filter((p) => ATTENDED.includes(p.attendance)).filter((p) => {
    const { done, total } = assessedCount(p, session.content || [], covered);
    return covered.length > 0 && done < total;
  });
  return { problems: session.closing_problems || [], unassessed: missing.length };
}

// « 1 module réussi sur 2 » after closing.
export function outcomeSummary(outcomes) {
  if (!outcomes?.length) return "";
  const passed = outcomes.filter((o) => o.passed).length;
  return `${passed} module${passed > 1 ? "s" : ""} réussi${passed > 1 ? "s" : ""} sur ${outcomes.length}`;
}

// Sessions grouped for the list: open ones first by date ascending (what comes next), then the others by date descending.
export function orderSessions(items) {
  const open = items.filter((s) => SESSION_OPEN.includes(s.status)).sort((a, b) => `${a.date}${a.start_time || ""}`.localeCompare(`${b.date}${b.start_time || ""}`));
  const rest = items.filter((s) => !SESSION_OPEN.includes(s.status)).sort((a, b) => b.date.localeCompare(a.date));
  return [...open, ...rest];
}

export function newIdempotencyKey() {
  return typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}
